import React from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import * as R from 'ramda/src/index';
import moment from 'moment';
import Popover from 'react-bootstrap/lib/Popover';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import {format24Hour, formatMonthDayPeriod} from 'lib/helpers/globalize-helper';
import {formatDuration, stopsText} from '../../common';
import classNames from 'classnames';
import LazyImage from 'helpers/LazyImage';

const getTransportationTypes = R.pipe(
  R.prop('transportationTypes'),
  R.uniq
);

const renderTransportationTypes = (transportationTypes) => (
  transportationTypes.length > 1 && <div className="transportation-types">
    {
      transportationTypes.map((type) => <span key={type} className={`transportation-type-${type} opacity-max`}/>)
    }
  </div>
);

const renderPopOverWithTrigger = ({popOverText, popoverId, placement, triggerNode}) => {
  const popover = <Popover id={popoverId}>{popOverText}</Popover>;

  return (
    <OverlayTrigger trigger={['hover', 'focus']} placement={placement} overlay={popover}>
      {triggerNode}
    </OverlayTrigger>
  );
};

const Leg = ({leg, translations, onClick}) => {
  const {
    nextDayArrival: nextDayArrivalMsg,
    afterMidnightArrival,
    differentAirports: differentAirportsMsg,
    technicalStopTitle,
    stops,
    time
  } = translations;
  const {
    hasTechnicalStop,
    hasDifferentAirport,
    isNextDayArrival,
    isMidnightArrival,
    segments,
    duration,
    stopsCount,
    legDifferentAirport
  } = leg;
  const hasStops = stopsCount > 0;
  const transportationTypes = getTransportationTypes(leg);

  const departureDestination = R.head(segments).route.departure;
  const arrivalDestination = R.last(segments).route.arrival;
  const depLocation = departureDestination.location.code;
  const arrLocation = arrivalDestination.location.code;
  const marketing = segments[0].transport.carriers.marketing;

  const stopTextColor = classNames({
    'text-muted': !hasDifferentAirport && !hasTechnicalStop,
    'text-danger': hasTechnicalStop || hasDifferentAirport
  });

  const diffLegAirportDepBgColor = classNames({
    'ml-sm-0': true,
    'ml-auto': true,
    'airport-name': true,
    'bg-secondary-light': legDifferentAirport.departure
  });
  const diffLegAirportArrBgColor = classNames({
    'airport-name': true,
    'bg-secondary-light': legDifferentAirport.arrival
  });
  
  const MidnightArrTextColor = classNames( 'leg-time ml-sm-1', {
    'text-danger': isMidnightArrival
  });

  const nextDayArrivalPopoverOptions = {
    popOverText: nextDayArrivalMsg,
    popoverId: 'next-day-arrival-popover',
    placement: 'top',
    triggerNode: <span className="leg-time ml-sm-1">
      {format24Hour(arrivalDestination.datetime)} <small className="nex-day-arr">+1</small>
    </span>
  };
  const afterMidnightArrivalPopoverOptions = {
    popOverText: afterMidnightArrival,
    popoverId: 'after-midnight-arrival-popover',
    placement: 'top',
    triggerNode: <span className={MidnightArrTextColor}>{format24Hour(arrivalDestination.datetime)}</span>
  };
  const differentAirportsPopoverOptions = {
    popOverText: differentAirportsMsg,
    popoverId: 'different-airports-popover',
    placement: 'top',
    triggerNode: <i className="icon icon-t-warning-e text-danger ml-1" />
  };
  const technicalStopsPopoverOptions = {
    popOverText: technicalStopTitle,
    popoverId: 'technical-stops-popover',
    placement: 'top',
    triggerNode: <i className="icon icon-wrench text-danger ml-1 d-none d-md-inline-block" />
  };

  return (
    <div className="segment-group-item" onClick={onClick}>
      <div className="grid align-items-center">
        <div className="grid-item-auto px-2 px-sm-3 text-center">
          <LazyImage  imgClass="img-fluid" src={marketing.logo} title="logo" alt="logo"/>
          <div className="airline-name d-none d-sm-block">{marketing.name}</div>
        </div>

        <div className="grid-item p-0 text-right leading-small">
          <div className="d-flex flex-column flex-sm-row align-items-baseline">
            <span className="leg-time mr-sm-1 ml-auto">{format24Hour(departureDestination.datetime)}</span>
            <span className={diffLegAirportDepBgColor}>{depLocation}</span>
          </div>
          <div className="text-muted text-small d-none d-sm-block">
            {formatMonthDayPeriod(departureDestination.datetime)}
          </div>
        </div>

        <div className="grid-item d-flex flex-column text-small text-center leading-none text-nowrap">
          <div className="d-none d-md-block">
            <span className={stopTextColor}>{stopsText(stopsCount, stops)}</span>
            {hasTechnicalStop && renderPopOverWithTrigger(technicalStopsPopoverOptions)}
            {hasDifferentAirport && renderPopOverWithTrigger(differentAirportsPopoverOptions)}
          </div>
          <div className="text-muted d-md-none">{formatDuration(moment.duration(duration, 'minutes'), time)}</div>
          <div className="stop-indicator">
            {
              hasStops ?
                R.range(0, stopsCount).map((count) =>
                  <span key={count} className="icon icon-shape-oval-2"/>
                )
                : <span className="icon icon-shape-oval-2 visibility-hidden"/>
            }
          </div>
          {renderTransportationTypes(transportationTypes)}
        </div>

        <div className="grid-item pl-0 leading-small">
          <div className="d-flex flex-column-reverse flex-sm-row align-items-baseline">
            <span className={diffLegAirportArrBgColor}>{arrLocation}</span>
            {
              isMidnightArrival
                ? renderPopOverWithTrigger(afterMidnightArrivalPopoverOptions)
                : isNextDayArrival
                  ? renderPopOverWithTrigger(nextDayArrivalPopoverOptions)
                  : <span className="leg-time ml-sm-1">{format24Hour(arrivalDestination.datetime)}</span>
            }
          </div>
          <div className="text-muted text-small d-none d-sm-block">
            {formatMonthDayPeriod(arrivalDestination.datetime)}
          </div>
        </div>
        <div className="grid-item text-center d-none d-md-block">
          {formatDuration(moment.duration(duration, 'minutes'), time)}
        </div>
      </div>
    </div>
  );
};

Leg.propTypes = {
  leg: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
};

renderPopOverWithTrigger.propTypes = {
  popOverText: PropTypes.string.isRequired,
  popoverId: PropTypes.string.isRequired,
  placement: PropTypes.string.isRequired,
  triggerNode: PropTypes.node.isRequired
};

export default observer(Leg);
